(() => {
  'use strict';
  if (window.__bbSearchRedirects) return;

  const configElement = document.querySelector('script[data-bb-search-redirects]');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (_) {
    return;
  }

  const debug = new URLSearchParams(location.search).get('bb_search_debug') === '1';
  const preview = Boolean(
    window.Shopify?.designMode
    || (window.Shopify?.theme?.role && window.Shopify.theme.role !== 'main')
    || new URLSearchParams(location.search).has('preview_theme_id')
  );
  const api = { version: '1.0.0', events: [], duplicateKeywords: [] };
  window.__bbSearchRedirects = api;

  const KEY = 'bb-search-redirect-v1';
  const TTL = 30 * 60 * 1000;
  const pending = [];
  let lastEnter = 0;
  const normalise = value => String(value || '').normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase().slice(0, 100);
  const valid = value => Array.from(value).length >= 3 && !/[^\s@]+@[^\s@]+\.[^\s@]+/.test(value);
  const pathname = value => {
    try {
      return new URL(value, location.origin).pathname.replace(/\/$/, '') || '/';
    } catch (_) {
      return '';
    }
  };
  const analyticsAllowed = () => {
    try {
      return typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function'
        || window.Shopify.customerPrivacy.analyticsProcessingAllowed();
    } catch (_) {
      return false;
    }
  };
  const storageAllowed = () => debug || analyticsAllowed();
  const currentPath = pathname(location.href);
  const rulesByKeyword = new Map();
  const duplicateKeywords = new Set();

  (Array.isArray(config.rules) ? config.rules : []).forEach((rule, ruleIndex) => {
    if (!rule || !rule.url || !rule.collectionHandle || !Array.isArray(rule.keywords)) return;
    rule.keywords.forEach(rawKeyword => {
      const keyword = normalise(rawKeyword);
      if (!valid(keyword) || duplicateKeywords.has(keyword)) return;
      const existing = rulesByKeyword.get(keyword);
      if (existing && existing.ruleIndex !== ruleIndex) {
        rulesByKeyword.delete(keyword);
        duplicateKeywords.add(keyword);
        return;
      }
      if (!existing) rulesByKeyword.set(keyword, { ...rule, ruleIndex });
    });
  });
  api.duplicateKeywords = Array.from(duplicateKeywords);

  function read() {
    if (!storageAllowed()) return null;
    try {
      const value = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (!value || Date.now() - value.startedAt >= TTL) {
        sessionStorage.removeItem(KEY);
        return null;
      }
      return value;
    } catch (_) {
      return null;
    }
  }

  function save(value) {
    if (!storageAllowed()) return;
    try {
      sessionStorage.setItem(KEY, JSON.stringify(value));
    } catch (_) { }
  }

  function clear() {
    try {
      sessionStorage.removeItem(KEY);
    } catch (_) { }
  }

  function emit(name, context, extra = {}) {
    if (!context || !valid(context.term) || !analyticsAllowed()) return;
    const params = {
      search_term: context.term,
      search_redirect_rule: context.ruleId,
      search_redirect_label: context.ruleLabel,
      search_redirect_destination: context.destinationPath,
      search_redirect_collection: context.destinationCollectionHandle,
      search_tracking_version: api.version,
      transport_type: 'beacon',
      ...extra
    };

    if (debug) {
      api.events.push({ name, params });
      if (api.events.length > 100) api.events.shift();
      return;
    }
    if (preview) return;

    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
      else if (pending.length < 50) pending.push({ name, params });
    } catch (_) { }
  }

  function flush() {
    if (!analyticsAllowed()) {
      pending.length = 0;
      return;
    }
    if (typeof window.gtag !== 'function') return;
    while (pending.length) {
      const event = pending.shift();
      try {
        window.gtag('event', event.name, event.params);
      } catch (_) { }
    }
  }

  function methodFor(event) {
    if (Date.now() - lastEnter < 600) return 'enter';
    if (!event.submitter) return 'enter';
    return event.submitter.closest?.('.search__submit') ? 'search_button' : '';
  }

  function handleSubmit(event) {
    const form = event.target;
    if (!form.matches?.('form[role="search"]') || event.defaultPrevented) return;
    if (!/(^|\/)search\/?$/.test(pathname(form.action))) return;
    if (event.submitter?.closest?.('.predictive-search__view-all, .js-submit')) return;

    const method = methodFor(event);
    if (!method) return;

    const term = normalise(form.querySelector('input[name="q"]')?.value);
    if (!valid(term)) return;

    clear();
    const rule = rulesByKeyword.get(term);
    if (!rule) return;

    event.preventDefault();
    const context = {
      id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      term,
      ruleId: rule.id || `rule-${rule.ruleIndex + 1}`,
      ruleLabel: rule.label || '',
      destinationPath: pathname(rule.url),
      destinationCollectionHandle: rule.collectionHandle,
      originPath: currentPath,
      interactionMethod: method,
      startedAt: Date.now(),
      landedAt: 0,
      returned: false,
      productViews: [],
      addToCartCount: 0
    };
    save(context);
    location.assign(rule.url);
  }

  function markLanding(context) {
    if (currentPath !== context.destinationPath || config.collectionHandle !== context.destinationCollectionHandle) return context;

    if (!context.landedAt) {
      context.landedAt = Date.now();
      save(context);
      emit('search_redirect', context, {
        action: 'redirect',
        interaction_method: context.interactionMethod,
        source_path: context.originPath
      });
      emit('search_redirect_landing', context, {
        action: 'collection_landing',
        interaction_method: context.interactionMethod,
        source_path: context.originPath,
        elapsed_seconds: Math.max(0, Math.round((context.landedAt - context.startedAt) / 1000))
      });
    }
    return context;
  }

  function markProduct(context) {
    if (!context.landedAt) return context;
    const collections = Array.isArray(config.productCollectionHandles) ? config.productCollectionHandles : [];
    if (!config.productHandle || !collections.includes(context.destinationCollectionHandle)) return context;
    if (!context.productViews.includes(config.productHandle)) {
      context.productViews.push(config.productHandle);
      save(context);
      emit('search_redirect_pdp_reach', context, {
        action: 'product_view',
        content_type: 'product',
        product_handle: config.productHandle,
        elapsed_seconds: Math.max(0, Math.round((Date.now() - context.startedAt) / 1000))
      });
    }
    return context;
  }

  function markReturn(context, action) {
    if (!context.landedAt || context.returned) return context;
    const returnedToOrigin = currentPath === context.originPath && currentPath !== context.destinationPath;
    const returnedToSearch = /(^|\/)search\/?$/.test(currentPath);
    if (!returnedToOrigin && !returnedToSearch) return context;

    context.returned = true;
    save(context);
    emit('search_redirect_return', context, {
      action,
      return_path: currentPath,
      elapsed_seconds: Math.max(0, Math.round((Date.now() - context.startedAt) / 1000))
    });
    return context;
  }

  function relevantPage(context) {
    if (config.collectionHandle === context.destinationCollectionHandle) return true;
    const collections = Array.isArray(config.productCollectionHandles) ? config.productCollectionHandles : [];
    return collections.includes(context.destinationCollectionHandle);
  }

  function trackAddToCart(event) {
    const context = read();
    if (!context || !context.landedAt || !relevantPage(context)) return;
    if (event.type === 'on:line-item:change') {
      const oldQuantity = Number(event.detail?.oldQuantity || 0);
      const newQuantity = Number(event.detail?.newQuantity || 0);
      if (newQuantity <= oldQuantity) return;
    }

    context.addToCartCount += 1;
    save(context);
    emit('search_redirect_add_to_cart', context, {
      action: 'add_to_cart',
      content_type: config.productHandle ? 'product' : 'collection',
      product_handle: config.productHandle || '',
      variant_id: String(event.detail?.variantId || ''),
      add_to_cart_number: context.addToCartCount,
      elapsed_seconds: Math.max(0, Math.round((Date.now() - context.startedAt) / 1000))
    });
  }

  function clearForDifferentSearch(event) {
    const input = event.target.closest?.('form[role="search"] input[name="q"]');
    if (!input) return;
    const context = read();
    if (context && normalise(input.value) !== context.term) clear();
  }

  function clearForPredictiveSelection(event) {
    if (event.target.closest?.('form[role="search"] .js-search-results a[href]')) clear();
  }

  function initialiseContext(action = 'page_load') {
    let context = read();
    if (!context) return;
    context = markLanding(context);
    context = markProduct(context);
    markReturn(context, action);
  }

  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches?.('form[role="search"] input[name="q"]')) lastEnter = Date.now();
  }, true);
  document.addEventListener('submit', handleSubmit);
  document.addEventListener('input', clearForDifferentSearch, { passive: true });
  document.addEventListener('click', clearForPredictiveSelection, true);
  document.addEventListener('on:cart:add', trackAddToCart);
  document.addEventListener('on:line-item:change', trackAddToCart);
  window.addEventListener('pageshow', event => initialiseContext(event.persisted ? 'back' : 'page_load'));
  document.addEventListener('visitorConsentCollected', () => {
    if (!analyticsAllowed() && !debug) {
      pending.length = 0;
      clear();
    }
  });

  initialiseContext();
  let tries = 0;
  const retry = setInterval(() => {
    flush();
    if (++tries >= 40) clearInterval(retry);
  }, 250);
})();
